import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import AppImage from '../../components/AppImage';
import { Phone, Users, Luggage, ChevronDown, ChevronUp } from 'lucide-react';
import { selfDrivePricing } from '../../data/cityPricingData';

const SelfDriveCity = () => {
  const { cityName } = useParams();
  const navigate = useNavigate();
  const [expandedVehicle, setExpandedVehicle] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [cityName]);

  const formattedCityName = cityName?.charAt(0)?.toUpperCase() + cityName?.slice(1)?.toLowerCase();

  // Get city-specific pricing from centralized data
  const vehicles = selfDrivePricing?.[cityName?.toLowerCase()] || [];


  const toggleDetails = (index) => {
    setExpandedVehicle(expandedVehicle === index ? null : index);
  };

  const handleEnquiry = (vehicle) => {
    navigate('/self-drive-enquiry', {
      state: {
        vehicle,
        city: formattedCityName,
        serviceType: 'self-drive'
      }
    });
  };

  return (
    <>
      <SEO
        title={`Self drive car Rental ${formattedCityName} - Rent a Car | Advait Tour and Travels`}
        description={`Rent self-drive cars in ${formattedCityName}. Choose from sedans, SUVs, and luxury vehicles. Flexible rental options and affordable rates.`}
        keywords={`self drive car rental ${cityName}, rent a car ${cityName}, ${cityName} car rental`}
        canonicalUrl={`${window.location?.origin}/self-drive/${cityName}`}
        ogTitle={`Self drive car Rental ${formattedCityName} - Rent a Car | Advait Tour and Travels`}
        ogDescription={`Rent self-drive cars in ${formattedCityName}. Choose from sedans, SUVs, and luxury vehicles. Flexible rental options and affordable rates.`}
        ogUrl={`${window.location?.origin}/self-drive/${cityName}`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AutoRental",
          "name": "Advait Tour and Travels",
          "description": `Self drive car rental in ${formattedCityName}`
        }} />

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-16 mb-12">
            <div className="container mx-auto px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white text-center">
                Self drive car Rental {formattedCityName}
              </h1>
            </div>
          </div>

          {/* Vehicle Cards */}
          <div className="container mx-auto px-4 mb-16">
            <div className="grid grid-cols-1 gap-6 max-w-6xl mx-auto">
              {vehicles?.map((vehicle, index) =>
              <div key={index} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200">
                  <div className="flex flex-col md:flex-row items-center gap-6 p-6">
                    {/* Vehicle Image */}
                    <div className="w-full md:w-64 h-48 flex-shrink-0">
                      <AppImage
                      src={vehicle?.image}
                      alt={vehicle?.alt}
                      className="w-full h-full object-contain" />

                    </div>

                    {/* Vehicle Details */}
                    <div className="flex-1 flex flex-col md:flex-row items-center justify-between gap-6 w-full">
                      {/* Car Type and Name */}
                      <div className="text-center md:text-left">
                        <p className="text-sm text-gray-500 font-medium uppercase mb-1">CAR TYPE</p>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{vehicle?.name}</h3>
                        <button
                        onClick={() => setExpandedVehicle(expandedVehicle === index ? null : index)}
                        className="text-green-600 font-semibold text-sm hover:underline flex items-center gap-1">

                          View Details
                          {expandedVehicle === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </div>

                      {/* Specifications */}
                      <div className="flex items-center gap-8">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg">
                            <Luggage className="text-blue-600" size={24} />
                          </div>
                          <div>
                            <p className="text-lg font-bold text-gray-900">{vehicle?.baggage} Baggage</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg">
                            <Users className="text-blue-600" size={24} />
                          </div>
                          <div>
                            <p className="text-lg font-bold text-gray-900">{vehicle?.seater} Seater</p>
                          </div>
                        </div>
                      </div>

                      {/* Price and Button */}
                      <div className="flex flex-col items-center gap-3">
                        <div className="text-right">
                          <p className="text-3xl font-bold text-green-600">₹{vehicle?.price?.toLocaleString('en-IN')}/-</p>
                          <p className="text-sm text-gray-500">Inc. of Taxes*</p>
                        </div>
                        <button
                        onClick={() => handleEnquiry(vehicle)}
                        className="px-8 py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-all duration-200 whitespace-nowrap">

                          Enquiry Now
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Details */}
                  {expandedVehicle === index &&
                <div className="px-6 pb-6 border-t border-gray-200 pt-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-semibold text-gray-700">Seating Capacity:</span>
                          <span className="ml-2 text-gray-600">{vehicle?.details?.seatingCapacity}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">AC:</span>
                          <span className="ml-2 text-gray-600">{vehicle?.details?.ac}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Rear AC Vents:</span>
                          <span className="ml-2 text-gray-600">{vehicle?.details?.rearAcVents}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Fuel Type:</span>
                          <span className="ml-2 text-gray-600">{vehicle?.details?.fuelType}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Luggage Carrier:</span>
                          <span className="ml-2 text-gray-600">{vehicle?.details?.luggageCarrier}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Phone Charger:</span>
                          <span className="ml-2 text-gray-600">{vehicle?.details?.phoneCharger}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Music System:</span>
                          <span className="ml-2 text-gray-600">{vehicle?.details?.musicSystem}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Smoking Allowed:</span>
                          <span className="ml-2 text-gray-600">{vehicle?.details?.smokingAllowed}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Pet Allowed:</span>
                          <span className="ml-2 text-gray-600">{vehicle?.details?.petAllowed}</span>
                        </div>
                        <div className="col-span-2 md:col-span-3">
                          <span className="font-semibold text-gray-700">Airport Parking Charges:</span>
                          <span className="ml-2 text-gray-600">{vehicle?.details?.airportParking}</span>
                        </div>
                      </div>
                    </div>
                }
                </div>
              )}
            </div>
          </div>

          {/* Visitor's Guide */}
          <div className="container mx-auto px-4 mb-16">
            <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Visitor's Guide
              </h2>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300">

                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>);

};

export default SelfDriveCity;